/**
 * Renders one event of a meet for every athlete: the local athlete from a locally predicted
 * sim (so a press moves them instantly), the others from server snapshots (interpolated).
 * Handles the start sequence, turns for field events, sounds, HUD and the scoreboard.
 */
import {
  ATTEMPT_CLOCK,
  createSim,
  eventById,
  HJ_IDEAL_ANGLE,
  LJ_IDEAL_ANGLE,
  racePose,
  SHOT_PUT,
  idealThrowAngle,
  runnerConfigFor,
  reconcile,
  simAction,
  simAdvance,
  simBeginTurn,
  simFinishTurn,
  simPress,
  simSnapshot,
  simTurnEndsAt,
  simTurnOver,
  type AthleteSim,
  type AthleteSnap,
  type EventDef,
  type EventStartMsg,
  type InputBatch,
  type PlayerInfo,
  type RunKey,
  type TurnMsg,
} from '@decathlon/shared';
import type { Synth } from '../audio.js';
import type { ControlEvent } from '../controls.js';
import type { Athlete, AthletePose } from '../game/athlete.js';
import { HJ_Z, LJ_Z } from '../game/events/field.js';
import { SP_X, SP_Z } from '../game/events/throws.js';
import type { GameScene } from '../game/scene.js';
import type { Hud, HudBoardRow } from '../screens/hud.js';
import { CadenceTracker, cadenceTips, type EventOutcome } from './controller.js';
import * as THREE from 'three';

export interface MeetEnv {
  scene: GameScene;
  synth: Synth;
  hud: Hud;
  meId: string | null;
  players: PlayerInfo[];
  start: EventStartMsg;
  /** Estimated server clock (ms). */
  serverNow(): number;
  send(batch: InputBatch): void;
}

interface View {
  projectilePitch: number;
  runX: number;
  runV: number;
  heading: number;
  hits: number;
  hurdle: number;
  x: number;
  y: number;
  comY: number;
  v: number;
  phase: string;
  angle: number;
  power: number;
  cadence: number;
  mark: number | null;
  bar: number;
  results: (number | null)[];
}

interface Remote {
  snap: AthleteSnap;
  at: number;
  view: View;
}

const emptyView = (): View => ({
  projectilePitch: 0,
  runX: 0,
  runV: 0,
  heading: 0,
  hits: 0,
  hurdle: 0,
  x: 0,
  y: 0,
  comY: 1,
  v: 0,
  phase: 'set',
  angle: 0,
  power: 0,
  cadence: 0,
  mark: null,
  bar: 0,
  results: [],
});
const viewOf = (s: AthleteSnap): View => ({
  projectilePitch: s.projectilePitch ?? 0,
  runX: s.runX ?? 0,
  runV: s.runV ?? 0,
  heading: s.heading ?? 0,
  hits: s.hits ?? 0,
  hurdle: s.hurdle ?? 0,
  x: s.x,
  y: s.y,
  comY: s.comY,
  v: s.v,
  phase: s.phase,
  angle: s.angle,
  power: s.power,
  cadence: s.cadence,
  mark: s.mark,
  bar: s.bar,
  results: s.results,
});

type FieldKind = 'lj' | 'hj' | 'sp';

export class MeetEventView {
  readonly ev: EventDef;
  private mySim: AthleteSim | null = null;
  private remotes = new Map<string, Remote>();
  private actors = new Map<string, Athlete>();
  private pending: InputBatch;
  private sequence = 0;
  private unacked: InputBatch[] = [];
  private renderedX: number | null = null;
  private renderedThrowX: number | null = null;
  private flushTimer: ReturnType<typeof setInterval>;
  private tracker = new CadenceTracker();
  private startPhase: 'marks' | 'set' | 'running' = 'marks';
  private heartbeatAt = 0;
  private activeId: string | null = null;
  private turn: TurnMsg | null = null;
  private turnAnnounced = '';
  private runout = new Map<string, { x: number; v: number }>();
  private finishedShown = false;
  private landedShown = false;
  private angles: number[] = [];
  private powers: number[] = [];
  private shotPoint = new THREE.Vector3();
  private handPoint = new THREE.Vector3();
  /** Summary for the results screen, filled as the event plays. */
  outcome: EventOutcome | null = null;

  constructor(private env: MeetEnv) {
    this.ev = eventById(env.start.event);
    this.pending = { eventIndex: env.start.index, presses: [], actions: [] };
    this.flushTimer = setInterval(() => this.flush(), 50);
    const me = env.players.find((p) => p.id === env.meId);
    if (me && !me.eliminated)
      this.mySim = createSim(this.ev, { startHeight: env.start.startHeight });
  }

  get turnBased(): boolean {
    return this.env.start.turnBased;
  }
  private get kind(): FieldKind | 'run' {
    return this.ev.id === 'pv'
      ? 'hj'
      : this.ev.kind === 'throw'
        ? 'sp'
        : this.ev.id === 'lj' || this.ev.id === 'hj'
          ? this.ev.id
          : 'run';
  }
  /** A bot is taking its turn: the player may skip watching it. */
  canSkip(): boolean {
    if (!this.turnBased || !this.activeId || this.activeId === this.env.meId) return false;
    return !!this.env.players.find((p) => p.id === this.activeId)?.isBot;
  }

  private get isMyTurn(): boolean {
    return !this.turnBased || (this.activeId !== null && this.activeId === this.env.meId);
  }

  /** Event time (s): sim time zero is the gun (track) or the event start (field). */
  private eventTime(): number {
    return (this.env.serverNow() - this.env.start.refAt) / 1000;
  }

  begin(): void {
    const { scene, synth, hud, players, start } = this.env;
    scene.field.sp.configure(this.ev.id);
    scene.field.hj.setVault(this.ev.id === 'pv');
    // Field events all run along +X; pin the camera so a spinning thrower does not swing it.
    scene.cameraYaw = this.turnBased ? 0 : null;
    scene.hurdles.visible = this.ev.id === '110h';
    scene.field.lj.mark(null);
    scene.field.sp.mark(null);
    scene.field.sp.setShot(null);
    for (const p of players) {
      if (p.eliminated) continue;
      const a = scene.addAthlete(p.id, p.kit, start.lanes[p.id] ?? 0);
      this.actors.set(p.id, a);
      a.setHeight(0);
      if (this.turnBased) this.placeWaiting(p.id, a);
      else {
        const lane = start.lanes[p.id] ?? 0;
        const pose = racePose(0, lane, this.raceDistance());
        a.place(pose.x, pose.z);
        a.face(pose.dx, pose.dz);
        a.setPose('set');
      }
    }
    scene.setFocus(
      this.env.meId && this.actors.has(this.env.meId) ? this.env.meId : (players[0]?.id ?? null),
    );
    scene.focusPoint = null;
    scene.race();
    synth.crowdStart();
    if (!this.turnBased) {
      hud.message('ON YOUR MARKS');
      synth.cue(false);
      synth.announce(`${this.ev.name}. On your marks.`);
      scene.stadium.setBoard([this.ev.name.toUpperCase(), '0.00', 'ON YOUR MARKS']);
    } else {
      scene.stadium.setBoard([this.ev.name.toUpperCase(), '—', 'FIRST ATHLETE']);
    }
  }

  dispose(): void {
    clearInterval(this.flushTimer);
    this.flush();
    this.env.hud.message(null);
    this.env.hud.banner(null);
    this.env.scene.field.lj.mark(null);
    this.env.scene.field.sp.mark(null);
    this.env.scene.field.sp.setShot(null);
    this.env.scene.field.hj.setBar(1.8);
    this.env.scene.focusPoint = null;
    this.env.scene.hurdles.visible = false;
    this.env.scene.field.hj.setVault(false);
    this.env.scene.cameraYaw = null;
  }

  private raceDistance(): number {
    return this.ev.id === '400m'
      ? 400
      : this.ev.id === '800m'
        ? 800
        : this.ev.id === '110h'
          ? 110
          : 100;
  }

  private fieldZ(): number {
    return this.kind === 'hj' ? HJ_Z : this.kind === 'sp' ? SP_Z : LJ_Z;
  }
  private fieldX0(): number {
    return this.kind === 'sp' ? SP_X : 0;
  }

  /** Waiting athletes stand in a line beside the runway or circle. */
  private placeWaiting(id: string, a: Athlete): void {
    const lane = this.env.start.lanes[id] ?? 0;
    a.place(this.fieldX0() - 2 - lane * 1.3, this.fieldZ() + 3.2);
    a.setHeight(0);
    a.setPose('idle');
  }

  // ------------------------------------------------------------ network in
  onSnapshot(athletes: AthleteSnap[], activeId: string | null, serverTime: number): void {
    for (const s of athletes) {
      const r = this.remotes.get(s.id);
      if (r) {
        r.snap = s;
        r.at = serverTime;
      } else this.remotes.set(s.id, { snap: s, at: serverTime, view: viewOf(s) });
    }
    if (this.turnBased) this.setActive(activeId);
  }

  correct(sim: AthleteSim, ack: number): void {
    if (!this.mySim) return;
    this.sequence = Math.max(this.sequence, ack + 1);
    this.unacked = this.unacked.filter((b) => (b.seq ?? -1) > ack);
    this.mySim = reconcile(sim, [...this.unacked, this.pending], this.eventTime(), this.isMyTurn);
    if (!this.mySim.done) this.outcome = null;
  }

  onTurn(msg: TurnMsg): void {
    this.turn = msg;
    this.setActive(msg.id);
    if (msg.id === this.env.meId && this.mySim) {
      // Our clock for this attempt: the server's turn start in event time.
      const t = (msg.refAt - this.env.start.refAt) / 1000;
      if (simTurnOver(this.mySim)) simFinishTurn(this.mySim);
      simBeginTurn(this.mySim, t);
    }
  }

  private setActive(id: string | null): void {
    if (
      id === this.activeId &&
      this.turnAnnounced === `${id}:${this.turn?.attempt ?? 0}:${this.turn?.bar ?? 0}`
    )
      return;
    if (this.activeId) {
      const prev = this.actors.get(this.activeId);
      if (prev) this.placeWaiting(this.activeId, prev);
      if (this.activeId === this.env.meId && this.mySim && simTurnOver(this.mySim))
        simFinishTurn(this.mySim);
    }
    this.activeId = id;
    this.env.hud.setSkippable(this.canSkip());
    this.renderedThrowX = null;
    this.landedShown = false;
    const { hud, synth, scene, players } = this.env;
    scene.field.lj.mark(null);
    scene.field.sp.mark(null);
    scene.field.sp.setShot(null);
    scene.focusPoint = null;
    if (!id) return;
    scene.setFocus(id);
    const p = players.find((x) => x.id === id);
    const mine = id === this.env.meId;
    const key = `${id}:${this.turn?.attempt ?? 0}:${this.turn?.bar ?? 0}`;
    if (this.turnAnnounced !== key) {
      this.turnAnnounced = key;
      if (this.kind === 'hj') scene.field.hj.setBar(this.turn?.bar || this.env.start.startHeight);
      hud.message(null);
      const detail =
        this.kind === 'hj'
          ? `bar at ${(this.turn?.bar ?? 0).toFixed(2)} m`
          : `attempt ${(this.turn?.attempt ?? 0) + 1} of 3`;
      if (mine) {
        hud.banner(`Your turn · ${detail}`, 'mine');
        synth.cue(true);
        synth.announce(`${p?.name ?? 'Athlete'}, your turn.`);
      } else {
        hud.banner(`${p?.name ?? 'Next athlete'} · ${detail}`);
        synth.announce(
          `${p?.name ?? 'Next athlete'}${this.kind === 'hj' ? `, bar at ${(this.turn?.bar ?? 0).toFixed(2).replace('.', ' metres ')}` : ''}.`,
        );
      }
      scene.stadium.setBoard([
        this.ev.name.toUpperCase(),
        this.kind === 'hj'
          ? `${(this.turn?.bar ?? 0).toFixed(2)} M`
          : `ATTEMPT ${(this.turn?.attempt ?? 0) + 1}`,
        (p?.name ?? '').toUpperCase().slice(0, 14),
      ]);
    }
  }

  // ------------------------------------------------------------ input
  control(e: ControlEvent): void {
    const { hud, synth } = this.env;
    const sim = this.mySim;
    if (!sim || sim.done) return;
    const t = this.eventTime();
    if (e.type === 'run') {
      if (!this.isMyTurn) return;
      if (sim.kind === 'run') {
        const s = sim.state;
        if (t < 0) {
          if (t >= -sim.cfg.falseStartWindow && !s.falseStart) {
            synth.falseStart();
            synth.groan();
            hud.message('FALSE START · 0.6 s PENALTY', 'bad');
            setTimeout(() => hud.message(null), 1500);
          } else hud.flashKey(e.key, false);
          simPress(sim, e.key, t);
          this.pending.presses.push({ key: e.key, t });
          return;
        }
        const q = s.pending;
        const last = q.length >= 2 ? q[q.length - 2]!.key : s.cadence.lastKey;
        const counts = last === -1 || last !== e.key;
        simPress(sim, e.key, t);
        hud.flashKey(e.key, counts && s.pending.length > 0);
        if (!counts) synth.dud();
      } else {
        const st = sim.state;
        const cad = sim.kind === 'sp' ? sim.state.cadence : sim.state.runner.cadence;
        const armed = sim.kind === 'sp' ? st.phase === 'power' : st.phase === 'runup';
        if (!armed) return;
        const before =
          sim.kind === 'sp' ? cad.lastKey : (sim.state.runner.pending.at(-1)?.key ?? cad.lastKey);
        simPress(sim, e.key, t);
        const counts = before === -1 || before !== e.key;
        hud.flashKey(e.key, counts);
        if (!counts) synth.dud();
        const me = this.actors.get(this.env.meId!);
        if (counts && me && me.currentPose === 'idle')
          me.setPose(sim.kind === 'sp' ? 'windup' : 'run');
      }
      this.pending.presses.push({ key: e.key, t });
    } else if (e.type === 'action') {
      if (!this.isMyTurn) return;
      const wasRunup = sim.kind !== 'run' && sim.state.phase === 'runup';
      simAction(sim, e.down, t);
      this.pending.actions.push({ down: e.down, t });
      if (this.ev.id === 'pv' && e.down && wasRunup && sim.state.phase === 'runup') {
        // Pressed on the approach: the plant is armed and happens at the box by itself.
        hud.flashKey('space', true);
        hud.banner('Pole armed: it plants at the box. Then release Space to push off', 'mine');
        return;
      }
      hud.flashKey('space', true);
      if (sim.kind !== 'run' && sim.state.phase === 'flight') {
        this.angles.push(sim.state.angle);
        if (sim.kind === 'sp') {
          this.powers.push(sim.state.releasePower);
          synth.put(sim.state.releasePower);
        } else synth.takeoff(Math.min(1, sim.state.takeoffV / sim.cfg.run.topSpeed));
      }
    }
  }

  private flush(): void {
    if (!this.pending.presses.length && !this.pending.actions.length) return;
    this.pending.seq = this.sequence++;
    this.unacked.push(structuredClone(this.pending));
    if (this.unacked.length > 100) this.unacked.shift();
    this.env.send(this.pending);
    this.pending = { eventIndex: this.env.start.index, presses: [], actions: [] };
  }

  // ------------------------------------------------------------ per frame
  frame(dt: number): void {
    const t = this.eventTime();
    if (this.turnBased) this.frameField(dt, t);
    else this.frameTrack(dt, t);
  }

  private myView(): View | null {
    if (!this.mySim) return null;
    return viewOf(simSnapshot(this.mySim, this.env.meId ?? 'me'));
  }

  /** Smooth a remote athlete towards its latest snapshot, extrapolating with its speed. */
  private remoteView(r: Remote, dt: number): View {
    const age = Math.min(0.4, (this.env.serverNow() - r.at) / 1000);
    const s = r.snap;
    const moving = s.phase === 'running' || s.phase === 'runup' || s.phase === 'flight';
    const targetX = moving ? s.x + s.v * age : s.x;
    const k = 1 - Math.exp(-dt * 10);
    const v = r.view;
    v.runV = s.runV ?? 0;
    const throwingRunup = s.phase === 'power' || s.phase === 'gauge';
    v.runX = (s.runX ?? 0) + (throwingRunup ? v.runV * age : 0);
    v.heading = s.heading ?? 0;
    v.projectilePitch = s.projectilePitch ?? 0;
    v.hits = s.hits ?? 0;
    v.hurdle = s.hurdle ?? 0;
    v.x += (targetX - v.x) * k;
    v.y += (s.y - v.y) * k;
    v.comY += (s.comY - v.comY) * k;
    v.v = s.v;
    v.phase = s.phase;
    v.angle = s.angle;
    v.power = s.power;
    v.cadence = s.cadence;
    v.mark = s.mark;
    v.bar = s.bar;
    v.results = s.results;
    return v;
  }

  private viewFor(id: string, dt: number): View {
    if (id === this.env.meId && this.mySim) return this.myView()!;
    const r = this.remotes.get(id);
    return r ? this.remoteView(r, dt) : emptyView();
  }

  // ------------------------------------------------------------ track events
  private frameTrack(dt: number, t: number): void {
    const { hud, synth, scene, start } = this.env;
    const cfg = this.mySim?.kind === 'run' ? this.mySim.cfg : runnerConfigFor(this.ev);
    const distance = cfg?.distance ?? 100;
    const topSpeed = cfg?.topSpeed ?? 10.6;
    const setT = (start.setAt - start.refAt) / 1000;
    if (this.startPhase === 'marks' && t >= setT) {
      this.startPhase = 'set';
      hud.message('SET');
      synth.cue(true);
      scene.stadium.setBoard([this.ev.name.toUpperCase(), '0.00', 'SET']);
    }
    if (this.startPhase === 'set') {
      if (performance.now() >= this.heartbeatAt) {
        synth.heartbeat();
        this.heartbeatAt = performance.now() + 900;
      }
      if (t >= 0) {
        this.startPhase = 'running';
        hud.message('BANG!', 'bang');
        synth.gun();
        setTimeout(
          () => this.startPhase === 'running' && !this.finishedShown && hud.message(null),
          700,
        );
        for (const a of this.actors.values()) a.setPose('run');
      }
    }
    if (this.mySim) simAdvance(this.mySim, t);

    const rows: HudBoardRow[] = [];
    let myV = 0;
    const myX = this.myView()?.x ?? 0;
    for (const p of this.env.players) {
      const a = this.actors.get(p.id);
      if (!a) continue;
      const mine = p.id === this.env.meId;
      const view = this.viewFor(p.id, dt);
      let x = view.x;
      let v = view.v;
      if (view.phase === 'finished') {
        let ro = this.runout.get(p.id);
        if (!ro) {
          ro = { x: Math.max(distance, x), v: Math.max(v, 4) };
          this.runout.set(p.id, ro);
          a.setPose('finish');
          if (mine) {
            this.finishedShown = true;
            const time = view.mark;
            synth.finish();
            synth.cheer();
            if (time !== null) {
              synth.announce(`${time.toFixed(2)} seconds.`);
              hud.message(time.toFixed(2), 'bang');
              scene.stadium.setBoard([this.ev.name.toUpperCase(), time.toFixed(2), 'FINISH']);
            }
            if (this.mySim?.kind === 'run') this.tracker.absorb(this.mySim.state);
          }
        }
        ro.v *= Math.exp(-dt * 1.6);
        ro.x += ro.v * dt;
        x = ro.x;
        v = ro.v;
      } else if (this.startPhase === 'running' && a.currentPose !== 'run') a.setPose('run');
      if (mine) {
        this.renderedX =
          this.renderedX === null
            ? x
            : this.renderedX + (x - this.renderedX) * (1 - Math.exp(-dt * 20));
        x = this.renderedX;
      }
      a.setHeight(view.y);
      if (view.y > 0.1) a.setPose('jump');
      const pose = racePose(x, start.lanes[p.id] ?? 0, distance);
      a.place(pose.x, pose.z);
      a.face(pose.dx, pose.dz);
      const strikes = a.update(dt, v, topSpeed);
      if (strikes > 0 && v > 0.5 && (mine || Math.abs(x - myX) < 12))
        synth.footstep(Math.min(1, v / topSpeed) * (mine ? 1 : 0.35));
      if (mine) myV = v;
      rows.push({
        id: p.id,
        name: p.name,
        kit: p.kit,
        me: mine,
        value: view.mark !== null ? view.mark.toFixed(2) : `${Math.min(distance, Math.floor(x))} m`,
        sort: view.mark !== null ? -1e6 + view.mark : -x,
      });
    }
    if (this.ev.id === '110h') {
      const mine = this.myView();
      hud.banner(
        `Hurdles ${mine?.hurdle ?? 0}/10 · hits ${mine?.hits ?? 0} · tap Space before each barrier`,
      );
    }
    scene.setFocusSpeed(myV);

    const me = this.myView();
    const cadence = me?.cadence ?? 0;
    const excite = this.finishedShown
      ? 0.9
      : Math.min(1, cadence / 12) * (0.3 + 0.7 * Math.min(1, (me?.x ?? 0) / distance));
    scene.stadium.excitement += (excite - scene.stadium.excitement) * Math.min(1, dt * 3);
    synth.setCrowd(scene.stadium.excitement);
    if (this.startPhase === 'running' && me && me.phase !== 'finished') {
      this.tracker.sample(cadence, dt);
      if (Math.floor(t * 10) !== Math.floor((t - dt) * 10))
        scene.stadium.setBoard([
          this.ev.name.toUpperCase(),
          Math.max(0, t).toFixed(1),
          `${Math.floor(me.x)} M`,
        ]);
    }
    rows.sort((a, b) => a.sort - b.sort);
    hud.setBoard(rows);
    const s = this.mySim?.kind === 'run' ? this.mySim.state : null;
    hud.update({
      primary: (me?.mark ?? Math.max(0, t)).toFixed(2),
      primaryLabel: 'Time',
      secondary: `${Math.min(distance, Math.floor(me?.x ?? 0))} m${distance > 400 ? ` · lap ${Math.min(Math.ceil(distance / 400), Math.floor((me?.x ?? 0) / 400) + 1)}/${Math.ceil(distance / 400)}` : ''}`,
      secondaryLabel: 'Distance',
      speed: myV,
      cadence,
      sweetCadence: cfg?.sweetCadence ?? 10,
      stamina: cfg?.stamina && s ? s.stamina : null,
      nextKey: s && !s.finished ? nextKey(s.pending.at(-1)?.key ?? s.cadence.lastKey) : -1,
      gauge: null,
      gaugeIdeal: 0,
      attempts: null,
    });
    if (this.mySim?.done && !this.outcome) this.finishOutcome();
  }

  // ------------------------------------------------------------ field events (turn by turn)
  private frameField(dt: number, t: number): void {
    const { hud, synth, scene } = this.env;
    const kind = this.kind as FieldKind;
    const z = this.fieldZ();
    const active = this.activeId;
    const mine = active !== null && active === this.env.meId;
    if (mine && this.mySim) simAdvance(this.mySim, t);
    scene.field.hj.update(dt);

    let activeView: View | null = null;
    let activeName = '';
    for (const p of this.env.players) {
      const a = this.actors.get(p.id);
      if (!a) continue;
      if (p.id !== active) {
        a.update(dt, 0, 10);
        a.groundAt(0);
        continue;
      }
      const view = this.viewFor(p.id, dt);
      activeView = view;
      activeName = p.name;
      if (kind === 'sp') this.renderThrower(a, view, dt, mine);
      else this.renderJumper(a, view, dt, kind, z, mine);
      this.landingMoment(view, kind, mine, p.name);
    }

    // Our own sim: close the turn locally when its pause ends (the server does the same).
    if (mine && this.mySim && simTurnOver(this.mySim) && t >= (simTurnEndsAt(this.mySim) ?? t))
      simFinishTurn(this.mySim);

    const view = activeView;
    const cadence = view?.cadence ?? 0;
    const excite =
      view?.phase === 'landed'
        ? 0.9
        : view?.phase === 'flight'
          ? 0.8
          : Math.min(1, cadence / 12) * 0.6;
    scene.stadium.excitement += (excite - scene.stadium.excitement) * Math.min(1, dt * 3);
    synth.setCrowd(scene.stadium.excitement);
    const winding = view?.phase === 'runup' || view?.phase === 'gauge' || view?.phase === 'power';
    if (mine && view && winding) this.tracker.sample(cadence, dt);
    synth.gaugeTick(mine && view?.phase === 'gauge' ? view.angle : null);

    const rows: HudBoardRow[] = this.env.players
      .filter((p) => this.actors.has(p.id))
      .map((p) => {
        const r =
          p.id === this.env.meId && this.mySim ? this.myView() : this.remotes.get(p.id)?.view;
        const mark = r?.mark ?? null;
        return {
          id: p.id,
          name: p.name,
          kit: p.kit,
          me: p.id === this.env.meId,
          value: mark !== null ? `${mark.toFixed(2)} m` : p.id === active ? '▶' : '—',
          sort: mark !== null ? -mark : 1,
        };
      })
      .sort((a, b) => a.sort - b.sort);
    hud.setBoard(rows);
    void activeName;
    this.updateFieldHud(kind, view, mine);
    if (this.mySim?.done && !this.outcome) this.finishOutcome();
  }

  private renderJumper(
    a: Athlete,
    view: View,
    dt: number,
    kind: 'lj' | 'hj',
    z: number,
    mine: boolean,
  ): void {
    const { synth, scene } = this.env;
    const isHj = kind === 'hj';
    const topSpeed =
      this.mySim && (this.mySim.kind === 'lj' || this.mySim.kind === 'hj')
        ? this.mySim.cfg.run.topSpeed
        : 9.9;
    const barX = 22;
    const pose: AthletePose =
      view.phase === 'runup'
        ? view.v > 0.3
          ? 'run'
          : 'idle'
        : view.phase === 'planted'
          ? 'jump'
          : view.phase === 'gauge'
            ? 'land'
            : view.phase === 'flight'
              ? isHj
                ? 'flop'
                : 'jump'
              : view.phase === 'landed'
                ? isHj
                  ? 'sit'
                  : 'land'
                : view.phase === 'foul'
                  ? view.x >= (isHj ? barX : 40)
                    ? isHj
                      ? 'sit'
                      : 'finish'
                    : 'land'
                  : 'idle';
    if (
      a.currentPose !== pose &&
      !(a.currentPose === 'run' && pose === 'idle' && view.phase === 'runup')
    )
      a.setPose(pose);
    const anc = a.anchor();
    a.place(view.x - anc.dx, z);
    const floor = isHj && view.x >= barX ? 0.6 : 0;
    a.setHeight(Math.max(floor, view.comY - anc.dy));
    const v = view.phase === 'runup' ? view.v : 0;
    const strikes = a.update(dt, v, topSpeed);
    if (strikes > 0 && v > 0.5) synth.footstep(Math.min(1, v / topSpeed) * (mine ? 1 : 0.5));
    scene.setFocusSpeed(v);
    if (this.ev.id === 'pv') scene.field.hj.setPole(view.x, view.comY, view.phase === 'planted');
    if (isHj) scene.field.hj.setBarHeight(view.bar || this.turn?.bar || this.env.start.startHeight);
  }

  private renderThrower(a: Athlete, view: View, dt: number, mine: boolean): void {
    const { scene, synth } = this.env;
    const javelin = this.ev.id === 'jt';
    const carrying = javelin && (view.phase === 'power' || view.phase === 'gauge');
    const pose: AthletePose = carrying
      ? 'javelin-run'
      : view.phase === 'power'
        ? view.cadence > 0.5
          ? 'windup'
          : 'idle'
        : view.phase === 'gauge'
          ? 'windup'
          : view.phase === 'flight' || view.phase === 'landed'
            ? 'throw'
            : view.phase === 'foul'
              ? 'idle'
              : 'idle';
    if (
      a.currentPose !== pose &&
      !(a.currentPose === 'windup' && pose === 'idle' && view.phase === 'power')
    )
      a.setPose(pose);
    // Smooth fixed simulation steps and network corrections before moving either the
    // athlete or camera. Reset this per attempt so a new run-up starts at its mark.
    this.renderedThrowX =
      this.renderedThrowX === null
        ? view.runX
        : this.renderedThrowX + (view.runX - this.renderedThrowX) * (1 - Math.exp(-dt * 20));
    a.place(SP_X + this.renderedThrowX, SP_Z);
    if (this.ev.id === 'dt' && (view.phase === 'power' || view.phase === 'gauge'))
      a.face(Math.cos((view.heading * Math.PI) / 180), Math.sin((view.heading * Math.PI) / 180));
    // The wind-up pumps with cadence; pass it as "speed" so the pose can breathe.
    a.update(dt, carrying ? view.runV : view.phase === 'power' ? view.cadence : 0, 10);
    a.groundAt(scene.field.sp.surfaceHeight);
    void mine;
    void synth;
    if (
      view.phase === 'flight' ||
      view.phase === 'landed' ||
      (view.phase === 'foul' && view.x > 0.5)
    ) {
      scene.field.sp.setShot({
        x: view.x,
        y: view.phase === 'flight' ? view.y : 0,
        pitch: view.projectilePitch,
      });
      if (view.phase === 'flight') {
        this.shotPoint.set(SP_X + view.x, 1 + view.y * 0.5, SP_Z);
        scene.focusPoint = this.shotPoint;
      }
    } else if (carrying) {
      scene.field.sp.carryJavelin(a.throwingHand(this.handPoint), a.root.rotation.y);
      // Foot alignment moves the rig vertically on every stride. Follow the runway
      // height instead, keeping that animation out of the camera's look target.
      this.shotPoint.set(a.root.position.x, scene.field.sp.surfaceHeight, SP_Z);
      scene.focusPoint = this.shotPoint;
    } else scene.field.sp.setShot(null);
    scene.setFocusSpeed(0);
  }

  /** Landing / foul moment (once per turn). */
  private landingMoment(view: View, kind: FieldKind, mine: boolean, name: string): void {
    const { hud, synth, scene } = this.env;
    if (!(view.phase === 'landed' || view.phase === 'foul') || this.landedShown) return;
    this.landedShown = true;
    // Throws: linger on where the implement came down for the result pause; the next turn
    // brings the camera back to the circle. Snapping straight back was jarring.
    if (kind === 'sp' && view.x > 0.5) {
      this.shotPoint.set(SP_X + view.x, 0.5, SP_Z);
      scene.focusPoint = this.shotPoint;
    } else scene.focusPoint = null;
    const last = view.results.at(-1) ?? null;
    const label = name.toUpperCase().slice(0, 14);
    const absorb = () => {
      if (!this.mySim || this.mySim.kind === 'run') return;
      this.tracker.absorb(this.mySim.kind === 'sp' ? this.mySim.state : this.mySim.state.runner);
    };
    if (view.phase === 'landed') {
      if (kind === 'hj') {
        synth.landMat();
        synth.clear();
      } else if (kind === 'sp') {
        synth.thud();
        scene.field.sp.mark(last ?? 0);
      } else {
        synth.landSand();
        scene.field.lj.mark(40 + (last ?? 0));
      }
      synth.cheer();
      if (mine) {
        hud.message(
          kind === 'hj' ? `CLEAR ${view.bar.toFixed(2)} m` : `${(last ?? 0).toFixed(2)} m`,
          'bang',
        );
        synth.announce(kind === 'hj' ? 'Clear!' : `${(last ?? 0).toFixed(2)} metres.`);
        absorb();
      }
      scene.stadium.setBoard([
        this.ev.name.toUpperCase(),
        kind === 'hj' ? `${view.bar.toFixed(2)} M CLEAR` : `${(last ?? 0).toFixed(2)} M`,
        label,
      ]);
    } else {
      if (kind === 'hj' && view.x >= 21.8) {
        scene.field.hj.knock();
        synth.barRattle();
      } else synth.whistle();
      if (kind === 'hj' && view.x >= 22) synth.landMat();
      synth.groan();
      if (mine) {
        hud.message(kind === 'hj' ? 'MISS' : 'FOUL', 'bad');
        synth.announce(kind === 'hj' ? 'Miss.' : 'Foul.');
        absorb();
      }
      scene.stadium.setBoard([this.ev.name.toUpperCase(), kind === 'hj' ? 'MISS' : 'FOUL', label]);
    }
  }

  private updateFieldHud(kind: FieldKind, view: View | null, mine: boolean): void {
    const { hud } = this.env;
    const me = this.myView();
    const sim = this.mySim && this.mySim.kind !== 'run' ? this.mySim : null;
    const myPhase = sim?.state.phase ?? 'done';
    const armed = kind === 'sp' ? 'power' : 'runup';
    const started = sim
      ? sim.kind === 'sp'
        ? sim.state.cadence.count > 0
        : sim.state.runner.cadence.count > 0
      : false;
    if (mine && this.ev.id === 'pv' && myPhase === 'planted')
      hud.banner('Planted! Release (or press) Space in the green window to push off', 'mine');
    const clockLeft =
      this.turn && mine
        ? Math.max(0, ATTEMPT_CLOCK - (this.env.serverNow() - this.turn.refAt) / 1000)
        : null;
    hud.setHeightChoice(
      mine && sim?.kind === 'hj' && !started && myPhase === 'runup' ? sim.state.bar : null,
      this.ev.id === 'pv' ? 0.1 : 0.05,
    );
    const showClock = mine && myPhase === armed && !started && clockLeft !== null;
    const toMark = kind === 'hj' ? 22 : 40;
    const ideal =
      kind === 'hj'
        ? this.ev.id === 'pv'
          ? 70
          : HJ_IDEAL_ANGLE
        : kind === 'sp' && this.mySim?.kind === 'sp'
          ? idealThrowAngle(this.mySim.cfg)
          : LJ_IDEAL_ANGLE;
    let secondary = '—';
    let secondaryLabel = kind === 'sp' ? 'Power' : kind === 'hj' ? 'To bar' : 'To board';
    if (showClock) {
      secondary = `${Math.ceil(clockLeft)} s`;
      secondaryLabel = 'Clock';
    } else if (kind === 'sp') {
      if (view && (view.phase === 'flight' || view.phase === 'landed')) {
        secondary = `${Math.max(0, view.x - (this.ev.id === 'jt' ? 30 : this.ev.id === 'dt' ? 0 : SHOT_PUT.boardOffset)).toFixed(1)} m`;
        secondaryLabel = 'Shot';
      } else if (view) {
        secondary =
          this.ev.id === 'dt' && view.phase === 'gauge'
            ? `${view.heading.toFixed(0)}° sector`
            : this.ev.id === 'jt'
              ? `${Math.max(0, 30 - view.runX).toFixed(1)} m · ${Math.round(view.power * 100)}%`
              : `${Math.round(Math.min(100, view.power * 100))} %`;
        if (this.ev.id === 'jt') secondaryLabel = 'To foul line';
      }
    } else if (view) secondary = `${Math.max(0, toMark - view.x).toFixed(1)} m`;
    const cad = sim ? (sim.kind === 'sp' ? sim.state.cadence : sim.state.runner.cadence) : null;
    const lastKey =
      sim && sim.kind !== 'sp'
        ? (sim.state.runner.pending.at(-1)?.key ?? cad!.lastKey)
        : (cad?.lastKey ?? -1);
    hud.update({
      primary:
        kind === 'hj'
          ? `${(me?.bar ?? this.turn?.bar ?? 0).toFixed(2)} m`
          : me?.mark !== null && me?.mark !== undefined
            ? `${me.mark.toFixed(2)} m`
            : '—',
      primaryLabel: kind === 'hj' ? 'Bar' : 'Best',
      secondary,
      secondaryLabel,
      speed:
        view && this.ev.id === 'jt' && (view.phase === 'power' || view.phase === 'gauge')
          ? view.runV
          : view && view.phase === 'runup'
            ? view.v
            : 0,
      cadence: mine && view ? view.cadence : 0,
      sweetCadence: 10,
      stamina: null,
      nextKey: mine && (myPhase === armed || myPhase === 'gauge') ? nextKey(lastKey) : -1,
      gauge:
        mine && (myPhase === 'gauge' || myPhase === 'planted' || myPhase === 'flight')
          ? (me?.angle ?? 0)
          : null,
      gaugeIdeal: ideal,
      attempts: me
        ? kind === 'hj'
          ? me.results.slice(-5).map((r) => ({
              label: r === null ? 'X' : r.toFixed(2),
              state: r === null ? ('foul' as const) : ('ok' as const),
            }))
          : Array.from({ length: 3 }, (_, i) => ({
              label:
                i < me.results.length
                  ? me.results[i] === null
                    ? 'X'
                    : me.results[i]!.toFixed(2)
                  : `${i + 1}`,
              state:
                i < me.results.length
                  ? me.results[i] === null
                    ? ('foul' as const)
                    : ('ok' as const)
                  : i === me.results.length && mine
                    ? ('current' as const)
                    : ('pending' as const),
            }))
        : null,
    });
  }

  /** Called when the server publishes the event results; returns our summary for the results screen. */
  finishOutcome(): EventOutcome {
    if (this.outcome) return this.outcome;
    const stats = this.tracker.stats();
    const tips = cadenceTips(stats, 10);
    const kind = this.kind;
    if (this.angles.length) {
      const ideal =
        kind === 'hj'
          ? this.ev.id === 'pv'
            ? 70
            : HJ_IDEAL_ANGLE
          : kind === 'sp' && this.mySim?.kind === 'sp'
            ? idealThrowAngle(this.mySim.cfg)
            : LJ_IDEAL_ANGLE;
      const avgA = this.angles.reduce((a, b) => a + b, 0) / this.angles.length;
      if (avgA > ideal + 8)
        tips.push(
          `Your release angle averaged ${avgA.toFixed(0)}°: too steep. Release nearer ${ideal}°.`,
        );
      else if (avgA < ideal - 8)
        tips.push(
          `Your release angle averaged ${avgA.toFixed(0)}°: too flat. Hold Space a little longer for ${ideal}°.`,
        );
      else tips.push(`Release angle averaged ${avgA.toFixed(0)}°, right in the window.`);
    }
    if (this.powers.length) {
      const avgP = this.powers.reduce((a, b) => a + b, 0) / this.powers.length;
      if (avgP < 0.8)
        tips.unshift(
          `Your wind-up averaged ${Math.round(avgP * 100)}% power. Hammer the keys for a good two seconds until the power reading tops out before you press Space.`,
        );
    }
    const me = this.myView();
    this.outcome = {
      mark: me?.mark ?? null,
      stats,
      details: [
        ...(me && me.results.length
          ? [
              {
                label: kind === 'hj' ? 'Heights' : 'Attempts',
                value: me.results
                  .map((r) => (r === null ? 'X' : `${r.toFixed(2)} m`))
                  .join('  ·  '),
              },
            ]
          : []),
        ...(this.angles.length
          ? [
              {
                label: kind === 'sp' ? 'Release angles' : 'Take-off angles',
                value: this.angles.map((a) => `${a.toFixed(0)}°`).join('  ·  '),
              },
            ]
          : []),
        ...(this.powers.length
          ? [
              {
                label: 'Power at release',
                value: this.powers.map((p) => `${Math.round(p * 100)}%`).join('  ·  '),
              },
            ]
          : []),
        { label: 'Average cadence', value: `${stats.avgCadence.toFixed(1)} / s` },
        { label: 'Presses counted', value: String(stats.presses) },
      ],
      tips,
    };
    return this.outcome;
  }
}

function nextKey(last: RunKey | -1): RunKey | -1 {
  if (last === -1) return -1;
  return last === 0 ? 1 : 0;
}
